import { FC } from 'react';
import { TeamMember } from '@demind-inc/core';
import { AddRounded, ExpandMoreRounded } from '@mui/icons-material';
import { Accordion, AccordionDetails, AccordionSummary, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import TeamMemberAvatar from '../../TeamMemberAvatar';
import TeamRoleChip from '../../TeamRoleChip';
import { TeamTaskGroup, TeamTaskWithOwner, TeamTasksGroupBy, getTeamTaskKey } from '../../utils';
import { BUCKET_COLORS } from '../constants';
import { getGroupColumns, getTaskProviderIcon } from '../helpers';
import TeamTaskRow from '../TeamTaskRow';
import type { TeamTasksSegment } from '../types';
import './TeamTaskGroupSection.scss';

interface TeamTaskGroupSectionProps {
  group: TeamTaskGroup;
  segment: TeamTasksSegment;
  groupBy: TeamTasksGroupBy;
  members: TeamMember[];
  currentUserId: string;
  isTaskStatusUpdating: boolean;
  isAssigningTask: boolean;
  onOpenTask: (taskItem: TeamTaskWithOwner) => void;
  onToggleTask: (taskItem: TeamTaskWithOwner, completed: boolean) => void;
  onAssignTask: (taskItem: TeamTaskWithOwner, assigneeUserId: string) => void;
  onCreateInGroup: (group: TeamTaskGroup, groupBy: TeamTasksGroupBy) => void;
}

const TeamTaskGroupSection: FC<TeamTaskGroupSectionProps> = ({
  group,
  segment,
  groupBy,
  members,
  currentUserId,
  isTaskStatusUpdating,
  isAssigningTask,
  onOpenTask,
  onToggleTask,
  onAssignTask,
  onCreateInGroup,
}) => {
  const { t } = useTranslation();
  const { hideOwner, hideBoard, gridTemplateColumns } = getGroupColumns(groupBy);
  const groupProviderIcon =
    groupBy === 'board'
      ? getTaskProviderIcon(group.provider || group.items[0]?.task.appFrom)
      : null;

  return (
    <Accordion className="team-tasks-view__accordion" defaultExpanded disableGutters elevation={0}>
      <AccordionSummary
        expandIcon={<ExpandMoreRounded className="team-tasks-view__accordion-icon" />}
        className="team-tasks-view__accordion-summary"
      >
        <div className="team-tasks-view__group-header">
          {group.bucket && (
            <span
              className="team-tasks-view__group-marker"
              style={{ background: BUCKET_COLORS[group.bucket] }}
              aria-hidden="true"
            />
          )}
          {group.owner && (
            <TeamMemberAvatar
              name={group.owner.name}
              initials={group.owner.initials}
              photoUrl={group.owner.photoUrl}
              size="medium"
            />
          )}
          {groupProviderIcon && (
            <img
              src={groupProviderIcon}
              alt=""
              aria-hidden="true"
              className="team-tasks-view__group-provider-icon"
            />
          )}
          <Typography variant="body2" component="span" className="team-tasks-view__group-title">
            {group.label}
          </Typography>
          {group.owner?.role && group.owner.role !== 'member' && (
            <TeamRoleChip role={group.owner.role} />
          )}
          <Typography variant="subtitle2" component="span" className="team-tasks-view__group-count">
            {group.items.length}
          </Typography>
          {segment === 'team' && (
            <button
              type="button"
              className="team-tasks-view__group-create-button"
              onClick={(event) => {
                event.stopPropagation();
                onCreateInGroup(group, groupBy);
              }}
            >
              <AddRounded className="team-tasks-view__group-create-icon" />
              <Typography variant="subtitle2" component="span">
                {t('team.tasks.newTask')}
              </Typography>
            </button>
          )}
        </div>
      </AccordionSummary>

      <AccordionDetails className="team-tasks-view__accordion-details">
        <div className="team-tasks-view__table">
          <div className="team-tasks-view__table-header" style={{ gridTemplateColumns }}>
            <span />
            <Typography variant="subtitle2" component="span">
              {t('team.tasks.table.task')}
            </Typography>
            {!hideOwner && (
              <Typography variant="subtitle2" component="span">
                {segment === 'team' ? t('team.tasks.table.assignee') : t('team.tasks.table.owner')}
              </Typography>
            )}
            {!hideBoard && (
              <Typography variant="subtitle2" component="span">
                {t('team.tasks.table.board')}
              </Typography>
            )}
            <Typography variant="subtitle2" component="span">
              {t('team.tasks.table.dueTime')}
            </Typography>
          </div>

          {group.items.map((taskItem, index) => (
            <TeamTaskRow
              key={getTeamTaskKey(taskItem, index)}
              taskItem={taskItem}
              segment={segment}
              members={members}
              currentUserId={currentUserId}
              gridTemplateColumns={gridTemplateColumns}
              hideOwner={hideOwner}
              hideBoard={hideBoard}
              isTaskStatusUpdating={isTaskStatusUpdating}
              isAssigningTask={isAssigningTask}
              onOpenTask={onOpenTask}
              onToggleTask={onToggleTask}
              onAssignTask={onAssignTask}
            />
          ))}
        </div>
      </AccordionDetails>
    </Accordion>
  );
};

export default TeamTaskGroupSection;
